import React, { useState, useEffect } from 'react';
import { Ship, Plus, Download, Filter, Edit2, Trash2, FileText, Search, ChevronUp, ChevronDown } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const MotorLaunches = () => {
  const [launches, setLaunches] = useState([]);
  const [clients, setClients] = useState([]);
  const [vessels, setVessels] = useState([]);
  const [settings, setSettings] = useState(null);
  const [motorLaunchRates, setMotorLaunchRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLaunch, setEditingLaunch] = useState(null);
  const [filterMotor, setFilterMotor] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Gelişmiş filtreler
  const [filters, setFilters] = useState({
    number: '',
    vessel_name: '',
    client_name: '',
    service_type: 'all',
    date_from: '',
    date_to: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: 'number', direction: 'desc' });
  
  const [formData, setFormData] = useState({
    motor_name: 'levend',
    vessel_name: '',
    client_id: '',
    agent_id: '', // Farklı acente seçimi
    service_type: 'ikmal',
    service_description: '',
    has_cargo: false,  // Yük taşıma var mı?
    cargo_tonnage: '',  // Yük taşıma tonajı
    cargo_rate: 70,     // Varsayılan ton başı ücret
    has_water: false,   // Su taşıma var mı?
    water_tonnage: '',  // Su taşıma tonajı (10 veya 20)
    location: '1. Demirsahası / 1st Anchorage Area',
    trip_date: new Date().toISOString().split('T')[0],
    departure_time: '08:00',
    return_time: '10:00',
    captain_name: '',
    waiting_hours: 0,
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [filterMotor]);

  const loadData = async () => {
    try {
      const params = filterMotor !== 'all' ? `?motor_name=${filterMotor}` : '';
      const [launchesRes, clientsRes, vesselsRes, settingsRes] = await Promise.all([
        api.get(`/motor-launches${params}`),
        api.get('/clients'),
        api.get('/vessels'),
        api.get('/settings')
      ]);
      
      setLaunches(launchesRes.data);
      setClients(clientsRes.data);
      setVessels(vesselsRes.data);
      setSettings(settingsRes.data);
      
      // Motor tarifelerini ayarlardan al
      if (settingsRes.data?.motor_launch_rates) {
        setMotorLaunchRates(settingsRes.data.motor_launch_rates.filter(r => r.active !== false));
      }
    } catch (error) {
      if (loading) {
        toast.error('Veriler yüklenemedi');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    const defaultLocation = motorLaunchRates.length > 0 
      ? `${motorLaunchRates[0].name_tr} / ${motorLaunchRates[0].name_en}`
      : '1. Demirsahası / 1st Anchorage Area';
    setFormData({
      motor_name: 'levend',
      vessel_name: '',
      client_id: '',
      agent_id: '',
      service_type: 'ikmal',
      service_description: '',
      has_cargo: false,
      cargo_tonnage: '',
      cargo_rate: 70,
      has_water: false,
      water_tonnage: '',
      location: motorLaunchRates.length > 0 
        ? `${motorLaunchRates[0].name_tr} / ${motorLaunchRates[0].name_en}`
        : '1. Demirsahası / 1st Anchorage Area',
      trip_date: new Date().toISOString().split('T')[0],
      departure_time: '08:00',
      return_time: '10:00',
      captain_name: '',
      waiting_hours: 0,
      notes: ''
    });
    setEditingLaunch(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Combine date and time - without Z to keep local time
      const tripDateTime = new Date(`${formData.trip_date}T00:00:00`);
      const departureDateTime = new Date(`${formData.trip_date}T${formData.departure_time}:00`);
      const returnDateTime = new Date(`${formData.trip_date}T${formData.return_time}:00`);
      
      const payload = {
        motor_name: formData.motor_name,
        vessel_name: formData.vessel_name,
        client_id: formData.client_id,
        agent_id: formData.agent_id || null,
        service_type: formData.service_type,
        service_description: formData.service_type === 'diger' ? formData.service_description : null,
        has_cargo: formData.has_cargo,
        cargo_tonnage: formData.has_cargo ? parseFloat(formData.cargo_tonnage) || null : null,
        cargo_rate: formData.has_cargo ? parseFloat(formData.cargo_rate) || 70 : null,
        has_water: formData.service_type === 'ikmal' ? formData.has_water : false,
        water_tonnage: formData.service_type === 'ikmal' && formData.has_water ? parseInt(formData.water_tonnage) || null : null,
        location: formData.location,
        trip_date: tripDateTime.toISOString(),
        departure_time: departureDateTime.toISOString(),
        return_time: returnDateTime.toISOString(),
        captain_name: formData.captain_name || null,
        waiting_hours: parseFloat(formData.waiting_hours) || 0,
        notes: formData.notes || null
      };

      if (editingLaunch) {
        await api.put(`/motor-launches/${editingLaunch.id}`, payload);
        toast.success('Sefer güncellendi');
      } else {
        await api.post('/motor-launches', payload);
        toast.success('Sefer eklendi');
      }

      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleEdit = (launch) => {
    setEditingLaunch(launch);
    
    const tripDate = new Date(launch.trip_date);
    const departureTime = new Date(launch.departure_time);
    const returnTime = new Date(launch.return_time);
    
    setFormData({
      motor_name: launch.motor_name,
      vessel_name: launch.vessel_name,
      client_id: launch.client_id,
      agent_id: launch.agent_id || '',
      service_type: launch.service_type,
      service_description: launch.service_description || '',
      has_cargo: launch.has_cargo || false,
      cargo_tonnage: launch.cargo_tonnage || '',
      cargo_rate: launch.cargo_rate || 70,
      location: launch.location,
      trip_date: tripDate.toISOString().split('T')[0],
      departure_time: departureTime.toTimeString().slice(0, 5),
      return_time: returnTime.toTimeString().slice(0, 5),
      captain_name: launch.captain_name || '',
      waiting_hours: launch.waiting_hours || 0,
      notes: launch.notes || ''
    });
    
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu seferi silmek istediğinizden emin misiniz?')) return;
    
    try {
      await api.delete(`/motor-launches/${id}`);
      toast.success('Sefer silindi');
      loadData();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  const generatePDF = async (launch) => {
    try {
      // Get PDF data
      const response = await api.get(`/motor-launches/${launch.id}/pdf-data`);
      const data = response.data;
      
      // Calculate cargo amount if exists
      const cargoAmount = data.has_cargo && data.cargo_tonnage ? (data.cargo_tonnage * (data.cargo_rate || 70)) : 0;
      const baseAmount = parseFloat(data.amount?.replace('$', '') || 0) - cargoAmount;
      
      // Create PDF content - Professional Motor Launch Bill
      const pdfContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            @page { size: A4; margin: 12mm; }
            * { box-sizing: border-box; }
            body { 
              font-family: 'Segoe UI', Arial, sans-serif; 
              margin: 0; 
              padding: 25px; 
              font-size: 13px;
              line-height: 1.5;
              color: #1e293b;
            }
            .header { 
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 20px;
              padding-bottom: 15px;
              border-bottom: 3px solid #1e40af;
            }
            .company-info {
              flex: 1;
            }
            .company-name { 
              font-size: 24px; 
              font-weight: bold; 
              color: #1e40af;
              letter-spacing: 1px;
              margin-bottom: 5px;
            }
            .company-subtitle {
              font-size: 11px;
              color: #64748b;
            }
            .bill-info {
              text-align: right;
            }
            .bill-number {
              font-size: 16px;
              font-weight: bold;
              color: #1e40af;
              margin-bottom: 5px;
            }
            .bill-date {
              font-size: 12px;
              color: #64748b;
            }
            .title { 
              font-size: 20px; 
              margin: 25px 0 20px 0; 
              font-weight: bold; 
              text-align: center;
              color: #fff;
              background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
              padding: 12px 20px;
              border-radius: 8px;
              text-transform: uppercase;
              letter-spacing: 3px;
            }
            .info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 15px;
              margin-bottom: 20px;
            }
            .info-box {
              background: #f8fafc;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              padding: 12px 15px;
            }
            .info-box-full {
              grid-column: 1 / -1;
            }
            .info-label {
              font-size: 10px;
              text-transform: uppercase;
              color: #64748b;
              font-weight: 600;
              margin-bottom: 4px;
              letter-spacing: 0.5px;
            }
            .info-value {
              font-size: 14px;
              font-weight: 600;
              color: #1e293b;
            }
            .details-table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
            }
            .details-table th {
              background: #1e40af;
              color: white;
              padding: 10px 12px;
              text-align: left;
              font-size: 11px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .details-table td {
              padding: 10px 12px;
              border-bottom: 1px solid #e2e8f0;
            }
            .details-table tr:nth-child(even) {
              background: #f8fafc;
            }
            .cargo-section {
              background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
              border: 2px solid #3b82f6;
              border-radius: 8px;
              padding: 15px;
              margin: 15px 0;
            }
            .cargo-title {
              font-size: 12px;
              font-weight: bold;
              color: #1e40af;
              margin-bottom: 10px;
              text-transform: uppercase;
              letter-spacing: 1px;
            }
            .cargo-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 15px;
            }
            .cargo-item {
              text-align: center;
            }
            .cargo-item-label {
              font-size: 10px;
              color: #64748b;
              text-transform: uppercase;
            }
            .cargo-item-value {
              font-size: 16px;
              font-weight: bold;
              color: #1e40af;
            }
            .amount-section {
              background: linear-gradient(135deg, #1e40af 0%, #3b82f6 100%);
              border-radius: 10px;
              padding: 20px;
              margin: 25px 0;
              color: white;
            }
            .amount-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 10px;
            }
            .amount-row {
              display: flex;
              justify-content: space-between;
              padding: 8px 0;
              border-bottom: 1px solid rgba(255,255,255,0.2);
            }
            .amount-row:last-child {
              border-bottom: none;
            }
            .amount-label {
              font-size: 12px;
              opacity: 0.9;
            }
            .amount-value {
              font-weight: bold;
              font-size: 14px;
            }
            .amount-total {
              grid-column: 1 / -1;
              text-align: center;
              padding-top: 15px;
              border-top: 2px solid rgba(255,255,255,0.3);
              margin-top: 10px;
            }
            .amount-total-label {
              font-size: 12px;
              opacity: 0.9;
              text-transform: uppercase;
              letter-spacing: 1px;
            }
            .amount-total-value {
              font-size: 32px;
              font-weight: bold;
              margin-top: 5px;
            }
            .signature-section {
              margin-top: 40px;
              display: flex;
              justify-content: space-between;
            }
            .signature-box {
              text-align: center;
              width: 200px;
            }
            .signature-line {
              border-top: 2px solid #1e293b;
              margin-top: 50px;
              padding-top: 8px;
            }
            .signature-title {
              font-weight: bold;
              font-size: 11px;
              color: #1e293b;
            }
            .signature-subtitle {
              font-size: 10px;
              color: #64748b;
            }
            .footer {
              position: fixed;
              bottom: 15px;
              left: 25px;
              right: 25px;
              text-align: center;
              font-size: 9px;
              color: #94a3b8;
              border-top: 1px solid #e2e8f0;
              padding-top: 8px;
            }
            .motor-badge {
              display: inline-block;
              background: #10b981;
              color: white;
              padding: 4px 12px;
              border-radius: 20px;
              font-size: 11px;
              font-weight: bold;
              text-transform: uppercase;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-info">
              <div class="company-name">ROTA MARITIME AGENCY</div>
              <div class="company-subtitle">Ship Agency Services • Sinop, Turkey</div>
            </div>
            <div class="bill-info">
              <div class="bill-number">${data.number}</div>
              <div class="bill-date">${data.date}</div>
            </div>
          </div>
          
          <div class="title">⚓ MOTOR LAUNCH BILL</div>
          
          <div class="info-grid">
            <div class="info-box">
              <div class="info-label">Vessel Name / Gemi Adı</div>
              <div class="info-value">${data.vessel_name}</div>
            </div>
            <div class="info-box">
              <div class="info-label">Client / Müşteri</div>
              <div class="info-value">${data.client_name}</div>
            </div>
            <div class="info-box">
              <div class="info-label">Agent / Acente</div>
              <div class="info-value">${data.agent}</div>
            </div>
            <div class="info-box">
              <div class="info-label">Motor</div>
              <div class="info-value"><span class="motor-badge">${data.motor_name.toUpperCase()}</span></div>
            </div>
            <div class="info-box info-box-full">
              <div class="info-label">Location / Gidilen Yer</div>
              <div class="info-value">${data.location}</div>
            </div>
          </div>
          
          <table class="details-table">
            <thead>
              <tr>
                <th>Service / Hizmet</th>
                <th>Departure / Kalkış</th>
                <th>Return / Varış</th>
                <th>Trip Hours / Sefer</th>
                <th>Waiting / Bekleme</th>
                <th>Total Hours</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>${data.description}</strong></td>
                <td>${data.departure_time}</td>
                <td>${data.return_time}</td>
                <td>${data.trip_hours} saat</td>
                <td>${data.waiting_time} saat</td>
                <td><strong>${data.total_hours} saat</strong></td>
              </tr>
            </tbody>
          </table>
          
          ${data.has_cargo && data.cargo_info ? `
          <div class="cargo-section">
            <div class="cargo-title">📦 Cargo Transport / Yük Taşıma</div>
            <div class="cargo-grid">
              <div class="cargo-item">
                <div class="cargo-item-label">Tonnage / Tonaj</div>
                <div class="cargo-item-value">${data.cargo_tonnage} TON</div>
              </div>
              <div class="cargo-item">
                <div class="cargo-item-label">Description / Açıklama</div>
                <div class="cargo-item-value">${data.cargo_info || '-'}</div>
              </div>
            </div>
          </div>
          ` : ''}
          
          ${data.captain ? `
          <div class="info-box" style="display: inline-block; margin-bottom: 20px;">
            <div class="info-label">Captain / Kaptan</div>
            <div class="info-value">${data.captain}</div>
          </div>
          ` : ''}
          
          <div class="signature-section">
            <div class="signature-box">
              <div class="signature-line">
                <div class="signature-title">Agency Stamp</div>
                <div class="signature-subtitle">Acente Kaşesi</div>
              </div>
            </div>
            <div class="signature-box">
              <div class="signature-line">
                <div class="signature-title">Captain's Signature</div>
                <div class="signature-subtitle">Kaptan İmzası</div>
              </div>
            </div>
          </div>
          
          <div class="footer">
            Rota Maritime Agency • Sinop, Turkey • Tel: +90 XXX XXX XXXX • Generated: ${new Date().toLocaleString('tr-TR')}
          </div>
        </body>
        </html>
      `;
      
      // Open in new window for printing
      const printWindow = window.open('', '_blank');
      printWindow.document.write(pdfContent);
      printWindow.document.close();
      
      // Wait a bit then trigger print
      setTimeout(() => {
        printWindow.print();
      }, 250);
      
      toast.success('PDF oluşturuldu - yazdırma penceresi açıldı');
    } catch (error) {
      toast.error('PDF oluşturulamadı');
    }
  };

  const exportToExcel = () => {
    const filteredData = getFilteredLaunches();
    
    // XLSX kütüphanesini import et
    import('xlsx').then(XLSX => {
      // Veriyi tablo formatına dönüştür
      const excelData = filteredData.map(launch => ({
        'Numara': launch.number || '-',
        'Motor': launch.motor_name === 'levend' ? 'Levend' : 'Rota',
        'Gemi Adı': launch.vessel_name,
        'Müşteri': launch.client_name,
        'Hizmet Türü': getServiceTypeLabel(launch.service_type, launch.service_description, launch),
        'Tarih': new Date(launch.trip_date).toLocaleDateString('tr-TR'),
        'Sefer Süresi (Saat)': parseFloat(launch.trip_hours.toFixed(2)),
        'Kalkış Yeri': launch.departure_point || '-',
        'Varış Yeri': launch.arrival_point || '-',
        'Kişi Sayısı': launch.person_count || '-',
        'Yük Var mı': launch.has_cargo ? 'Evet' : 'Hayır',
        'Yük Miktarı (Ton)': launch.cargo_tonnage || '-',
        'Notlar': launch.notes || '-',
        'Oluşturan': launch.created_by_name || '-',
        'Oluşturulma': new Date(launch.created_at).toLocaleDateString('tr-TR')
      }));
      
      // Özet satırları
      const levendTrips = filteredData.filter(l => l.motor_name === 'levend');
      const rotaTrips = filteredData.filter(l => l.motor_name === 'rota');
      const totalHours = filteredData.reduce((sum, l) => sum + (l.trip_hours || 0), 0);
      
      // Boş satır ve özet ekle
      excelData.push({});
      excelData.push({ 'Numara': '--- ÖZET ---' });
      excelData.push({ 'Numara': 'Toplam Sefer', 'Motor': filteredData.length });
      excelData.push({ 'Numara': 'Levend Seferleri', 'Motor': levendTrips.length });
      excelData.push({ 'Numara': 'Rota Seferleri', 'Motor': rotaTrips.length });
      excelData.push({ 'Numara': 'Toplam Süre', 'Motor': `${totalHours.toFixed(1)} saat` });
      
      // Worksheet oluştur
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      // Sütun genişliklerini ayarla
      ws['!cols'] = [
        { wch: 15 }, // Numara
        { wch: 10 }, // Motor
        { wch: 25 }, // Gemi Adı
        { wch: 25 }, // Müşteri
        { wch: 30 }, // Hizmet Türü
        { wch: 12 }, // Tarih
        { wch: 18 }, // Sefer Süresi
        { wch: 15 }, // Kalkış
        { wch: 15 }, // Varış
        { wch: 12 }, // Kişi Sayısı
        { wch: 12 }, // Yük Var mı
        { wch: 15 }, // Yük Miktarı
        { wch: 30 }, // Notlar
        { wch: 15 }, // Oluşturan
        { wch: 12 }  // Oluşturulma
      ];
      
      // Workbook oluştur
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Motor Seferleri');
      
      // Tarih bazlı dosya adı
      const today = new Date().toISOString().split('T')[0];
      const fileName = `motor_seferleri_${today}.xlsx`;
      
      // İndir
      XLSX.writeFile(wb, fileName);
      
      toast.success(`Excel raporu indirildi (${filteredData.length} kayıt)`);
    }).catch(err => {
      console.error('XLSX yüklenemedi:', err);
      toast.error('Excel oluşturulurken hata oluştu');
    });
  };

  const getServiceTypeLabel = (type, serviceDescription, launch) => {
    const labels = {
      'gemi_kontrolu': 'Gemi Kontrolü',
      'ikmal': 'İkmal',
      'su_ikmali': 'Su İkmali',
      'diger': 'Diğer'
    };
    let label = labels[type] || type;
    
    // "Diğer" seçilmişse ve açıklama varsa, açıklamayı göster
    if (type === 'diger' && serviceDescription) {
      label = serviceDescription;
    }
    
    // Su ikmali varsa tonajı ekle
    if (type === 'su_ikmali' && launch?.water_tonnage) {
      label += ` (${launch.water_tonnage} ton)`;
    }
    
    // Yük taşıma varsa ekle
    if (launch?.has_cargo && launch?.cargo_tonnage) {
      label += ` + Taşıma (${launch.cargo_tonnage} ton)`;
    }
    
    return label;
  };

  // Sıralama fonksiyonu
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getFilteredLaunches = () => {
    let filtered = launches.filter(launch => {
      // Genel arama
      const matchesSearch = 
        launch.vessel_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        launch.client_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (launch.number && launch.number.toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Gelişmiş filtreler
      const matchesNumber = !filters.number || (launch.number && launch.number.toLowerCase().includes(filters.number.toLowerCase()));
      const matchesVessel = !filters.vessel_name || launch.vessel_name.toLowerCase().includes(filters.vessel_name.toLowerCase());
      const matchesClient = !filters.client_name || launch.client_name.toLowerCase().includes(filters.client_name.toLowerCase());
      const matchesServiceType = filters.service_type === 'all' || launch.service_type === filters.service_type;
      
      // Tarih filtresi
      let matchesDate = true;
      if (filters.date_from || filters.date_to) {
        const launchDate = new Date(launch.trip_date);
        if (filters.date_from) {
          matchesDate = matchesDate && launchDate >= new Date(filters.date_from);
        }
        if (filters.date_to) {
          matchesDate = matchesDate && launchDate <= new Date(filters.date_to);
        }
      }
      
      return matchesSearch && matchesNumber && matchesVessel && matchesClient && matchesServiceType && matchesDate;
    });
    
    // Sıralama
    filtered.sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Numara için özel sıralama
      if (sortConfig.key === 'number') {
        const aNum = parseInt(aVal?.split('-').pop() || '0');
        const bNum = parseInt(bVal?.split('-').pop() || '0');
        return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
      }
      
      // Tarih için
      if (sortConfig.key === 'trip_date') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  };

  const filteredLaunches = getFilteredLaunches();

  if (loading) {
    return (
      <div className="content">
        <div className="topbar">
          <h1 className="topbar-title">Acente Motoru Seferleri</h1>
        </div>
        <div style={{ padding: '2rem', textAlign: 'center' }}>Yükleniyor...</div>
      </div>
    );
  }

  // Motor seferleri tablosu render fonksiyonu
  const renderMotorTable = (launchList, title, bgColor) => {
    if (launchList.length === 0) return null;
    
    return (
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          padding: '0.75rem 1rem', 
          backgroundColor: bgColor, 
          color: 'white',
          borderRadius: '8px 8px 0 0',
          fontWeight: '600'
        }}>
          <span>{title}</span>
          <span style={{ 
            backgroundColor: 'rgba(255,255,255,0.2)', 
            padding: '0.25rem 0.75rem', 
            borderRadius: '12px',
            fontSize: '0.875rem'
          }}>
            {launchList.length}
          </span>
        </div>
        <div className="table-container" style={{ borderRadius: '0 0 8px 8px', border: `1px solid ${bgColor}`, borderTop: 'none' }}>
          <table className="table table-sm" style={{ marginBottom: 0 }}>
            <thead>
              <tr style={{ fontSize: '0.85rem', backgroundColor: '#f8fafc' }}>
                <th style={{ padding: '0.75rem 0.5rem' }}>Numara</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Gemi</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Müşteri</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Hizmet</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Tarih</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Gidiş</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Dönüş</th>
                <th style={{ padding: '0.75rem 0.5rem' }}>Süre</th>
                <th style={{ padding: '0.75rem 0.5rem', textAlign: 'center' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {launchList.map((launch) => (
                <tr key={launch.id} style={{ fontSize: '0.9rem' }}>
                  <td style={{ padding: '0.5rem', fontWeight: '500' }}>{launch.number || '-'}</td>
                  <td style={{ padding: '0.5rem' }}><strong>{launch.vessel_name}</strong></td>
                  <td style={{ padding: '0.5rem' }}>{launch.client_name}</td>
                  <td style={{ padding: '0.5rem' }}>{getServiceTypeLabel(launch.service_type, launch.service_description, launch)}</td>
                  <td style={{ padding: '0.5rem' }}>{new Date(launch.trip_date).toLocaleDateString('tr-TR')}</td>
                  <td style={{ padding: '0.5rem' }}>{new Date(launch.departure_time).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</td>
                  <td style={{ padding: '0.5rem' }}>{new Date(launch.return_time).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</td>
                  <td style={{ padding: '0.5rem' }}>{launch.trip_hours.toFixed(1)}h</td>
                  <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                      <button className="btn btn-sm btn-success" onClick={() => generatePDF(launch)} style={{ padding: '0.25rem 0.5rem' }} title="PDF"><FileText size={14} /></button>
                      <button className="btn btn-sm btn-secondary" onClick={() => handleEdit(launch)} style={{ padding: '0.25rem 0.5rem' }}><Edit2 size={14} /></button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(launch.id)} style={{ padding: '0.25rem 0.5rem' }}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="content">
      <div className="topbar">
        <h1 className="topbar-title">
          <Ship size={24} style={{ marginRight: '0.5rem' }} />
          Acente Motoru Seferleri
        </h1>
        <div className="topbar-actions">
          <button 
            className="btn btn-secondary" 
            onClick={exportToExcel}
            disabled={filteredLaunches.length === 0}
          >
            <Download size={18} />
            Excel İndir
          </button>
          <button 
            className="btn btn-primary" 
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            <Plus size={18} />
            Yeni Sefer
          </button>
        </div>
      </div>

      <div style={{ padding: '2rem' }}>
        {/* Filters */}
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-body">
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
              <div style={{ flex: '1', minWidth: '250px' }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="🔍 Gemi, müşteri veya numara ile ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  className={`btn ${filterMotor === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilterMotor('all')}
                >
                  Tümü ({launches.length})
                </button>
                <button
                  className={`btn ${filterMotor === 'levend' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilterMotor('levend')}
                >
                  Levend ({launches.filter(l => l.motor_name === 'levend').length})
                </button>
                <button
                  className={`btn ${filterMotor === 'rota' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilterMotor('rota')}
                >
                  Rota ({launches.filter(l => l.motor_name === 'rota').length})
                </button>
              </div>
            </div>
            
            {/* Gelişmiş Filtreler */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
              gap: '0.75rem',
              padding: '1rem',
              backgroundColor: '#f8fafc',
              borderRadius: '8px'
            }}>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b', marginBottom: '0.25rem', display: 'block' }}>Hizmet Türü</label>
                <select 
                  className="form-control form-control-sm"
                  value={filters.service_type}
                  onChange={(e) => setFilters({...filters, service_type: e.target.value})}
                >
                  <option value="all">Tümü</option>
                  <option value="gemi_kontrolu">Gemi Kontrolü</option>
                  <option value="ikmal">İkmal</option>
                  <option value="su_ikmali">Su İkmali</option>
                  <option value="diger">Diğer</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b', marginBottom: '0.25rem', display: 'block' }}>Başlangıç Tarihi</label>
                <input 
                  type="date" 
                  className="form-control form-control-sm"
                  value={filters.date_from}
                  onChange={(e) => setFilters({...filters, date_from: e.target.value})}
                />
              </div>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748b', marginBottom: '0.25rem', display: 'block' }}>Bitiş Tarihi</label>
                <input 
                  type="date" 
                  className="form-control form-control-sm"
                  value={filters.date_to}
                  onChange={(e) => setFilters({...filters, date_to: e.target.value})}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button 
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setFilters({ number: '', vessel_name: '', client_name: '', service_type: 'all', date_from: '', date_to: '' })}
                  style={{ width: '100%' }}
                >
                  Temizle
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Trips Table */}
        {filteredLaunches.length === 0 ? (
          <div className="card">
            <div className="card-body" style={{ padding: '3rem', textAlign: 'center' }}>
              <Ship size={48} style={{ color: '#ccc', marginBottom: '1rem' }} />
              <p style={{ color: '#666' }}>
                {searchTerm ? 'Arama kriterlerine uygun sefer bulunamadı' : 'Henüz sefer kaydı bulunmuyor'}
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Özet Kartları */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
              <div style={{ background: 'linear-gradient(135deg, #1a365d, #2563eb)', padding: '1rem', borderRadius: '8px', color: 'white', textAlign: 'center' }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{filteredLaunches.filter(l => l.motor_name === 'levend').length}</div>
                <div style={{ opacity: 0.9, fontSize: '0.875rem' }}>Levend</div>
              </div>
              <div style={{ background: 'linear-gradient(135deg, #059669, #10b981)', padding: '1rem', borderRadius: '8px', color: 'white', textAlign: 'center' }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{filteredLaunches.filter(l => l.motor_name === 'rota').length}</div>
                <div style={{ opacity: 0.9, fontSize: '0.875rem' }}>Rota</div>
              </div>
              <div style={{ background: 'linear-gradient(135deg, #0891b2, #06b6d4)', padding: '1rem', borderRadius: '8px', color: 'white', textAlign: 'center' }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{filteredLaunches.filter(l => l.service_type === 'su_ikmali').length}</div>
                <div style={{ opacity: 0.9, fontSize: '0.875rem' }}>💧 Su İkmali</div>
              </div>
              <div style={{ background: 'linear-gradient(135deg, #ea580c, #f97316)', padding: '1rem', borderRadius: '8px', color: 'white', textAlign: 'center' }}>
                <div style={{ fontSize: '1.75rem', fontWeight: 'bold' }}>{filteredLaunches.filter(l => l.service_type !== 'su_ikmali').length}</div>
                <div style={{ opacity: 0.9, fontSize: '0.875rem' }}>İkmal/Diğer</div>
              </div>
            </div>

            {/* Motor Bazlı Gruplandırılmış Tablolar */}
            {renderMotorTable(filteredLaunches.filter(l => l.motor_name === 'levend'), '⚓ Levend Seferleri', '#1a365d')}
            {renderMotorTable(filteredLaunches.filter(l => l.motor_name === 'rota'), '🚤 Rota Seferleri', '#059669')}
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" style={{ 
          zIndex: 1000,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div className="modal-content" style={{ 
            width: '95%',
            maxWidth: '1100px',
            maxHeight: '90vh',
            boxShadow: '0 25px 80px rgba(0,0,0,0.5)',
            border: 'none',
            borderRadius: '16px',
            backgroundColor: '#ffffff',
            overflow: 'hidden'
          }} onClick={(e) => e.stopPropagation()}>
            
            {/* Header */}
            <div className="modal-header" style={{ 
              background: 'linear-gradient(135deg, #1a365d 0%, #2563eb 100%)',
              color: 'white',
              padding: '1.5rem 2rem',
              borderBottom: 'none'
            }}>
              <h2 className="modal-title" style={{ color: 'white', fontSize: '1.5rem', margin: 0, fontWeight: '600' }}>
                🚤 {editingLaunch ? 'Sefer Düzenle' : 'Yeni Motor Seferi Ekle'}
              </h2>
              <button className="modal-close" onClick={() => setShowModal(false)} style={{ 
                color: 'white', 
                fontSize: '28px',
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>×</button>
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="modal-body" style={{ 
                maxHeight: '65vh', 
                overflowY: 'auto', 
                backgroundColor: '#f8fafc',
                padding: '2rem'
              }}>
                
                {/* BÖLÜM 1: Motor ve Hizmet Bilgileri */}
                <div style={{ 
                  backgroundColor: '#fff', 
                  borderRadius: '12px', 
                  padding: '1.5rem',
                  marginBottom: '1.5rem',
                  border: '1px solid #e2e8f0'
                }}>
                  <h3 style={{ 
                    fontSize: '1rem', 
                    fontWeight: '600', 
                    color: '#1a365d', 
                    marginBottom: '1.25rem',
                    paddingBottom: '0.75rem',
                    borderBottom: '2px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span style={{ fontSize: '1.2rem' }}>⚙️</span> Motor ve Hizmet Bilgileri
                  </h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label htmlFor="motor_name" style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                        Motor Seçimi <span style={{ color: '#dc2626' }}>*</span>
                      </label>
                      <select
                        id="motor_name"
                        className="form-control"
                        value={formData.motor_name}
                        onChange={(e) => setFormData({ ...formData, motor_name: e.target.value })}
                        required
                        style={{ padding: '0.75rem', fontSize: '1rem', borderRadius: '8px' }}
                      >
                        <option value="levend">🚤 Levend</option>
                        <option value="rota">🚤 Rota</option>
                      </select>
                    </div>
                    
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label htmlFor="service_type" style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                        Hizmet Türü <span style={{ color: '#dc2626' }}>*</span>
                      </label>
                      <select
                        id="service_type"
                        className="form-control"
                        value={formData.service_type}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          service_type: e.target.value,
                          service_description: e.target.value === 'diger' ? formData.service_description : '',
                          water_tonnage: e.target.value === 'su_ikmali' ? '10' : ''
                        })}
                        required
                        style={{ padding: '0.75rem', fontSize: '1rem', borderRadius: '8px' }}
                      >
                        <option value="ikmal">⛽ İkmal</option>
                        <option value="su_ikmali">💧 Su İkmali</option>
                        <option value="diger">📝 Diğer (Özel Hizmet)</option>
                        <option value="gemi_kontrolu">🔍 Gemi Kontrolü</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Diğer seçildiğinde açıklama alanı */}
                  {formData.service_type === 'diger' && (
                    <div className="form-group" style={{ marginTop: '1.25rem', marginBottom: 0 }}>
                      <label htmlFor="service_description" style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                        Hizmet Açıklaması <span style={{ color: '#dc2626' }}>*</span>
                      </label>
                      <textarea
                        id="service_description"
                        className="form-control"
                        value={formData.service_description}
                        onChange={(e) => setFormData({ ...formData, service_description: e.target.value })}
                        placeholder="Yapılan hizmetin detaylı açıklaması..."
                        rows={3}
                        required
                        style={{ 
                          padding: '0.75rem',
                          fontSize: '1rem',
                          borderRadius: '8px',
                          resize: 'vertical',
                          minHeight: '80px'
                        }}
                      />
                    </div>
                  )}
                  
                  {/* Su İkmali seçildiğinde tonaj seçimi */}
                  {formData.service_type === 'su_ikmali' && (
                    <div style={{ 
                      marginTop: '1.5rem', 
                      padding: '1.25rem', 
                      backgroundColor: '#ecfeff', 
                      borderRadius: '8px',
                      border: '2px solid #06b6d4'
                    }}>
                      <label style={{ fontWeight: '600', marginBottom: '0.75rem', display: 'block', fontSize: '1rem' }}>
                        💧 Su Miktarı Seçin <span style={{ color: '#dc2626' }}>*</span>
                      </label>
                      <div style={{ display: 'flex', gap: '1rem' }}>
                        <label style={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          alignItems: 'center', 
                          gap: '0.5rem',
                          padding: '1rem 1.5rem',
                          backgroundColor: formData.water_tonnage === '10' ? '#cffafe' : '#f1f5f9',
                          borderRadius: '12px',
                          border: formData.water_tonnage === '10' ? '3px solid #06b6d4' : '2px solid #e2e8f0',
                          cursor: 'pointer',
                          flex: 1,
                          transition: 'all 0.2s'
                        }}>
                          <input
                            type="radio"
                            name="water_tonnage"
                            value="10"
                            checked={formData.water_tonnage === '10'}
                            onChange={(e) => setFormData({ ...formData, water_tonnage: e.target.value })}
                            style={{ display: 'none' }}
                          />
                          <span style={{ fontSize: '2rem' }}>🚰</span>
                          <span style={{ fontWeight: '700', fontSize: '1.25rem' }}>10 Ton</span>
                          <span style={{ 
                            color: '#0891b2', 
                            fontWeight: '700', 
                            fontSize: '1.1rem',
                            backgroundColor: '#cffafe',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '999px'
                          }}>
                            650 USD
                          </span>
                          <span style={{ color: '#64748b', fontSize: '0.8rem' }}>(65 USD x 10 ton)</span>
                        </label>
                        <label style={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          alignItems: 'center', 
                          gap: '0.5rem',
                          padding: '1rem 1.5rem',
                          backgroundColor: formData.water_tonnage === '20' ? '#cffafe' : '#f1f5f9',
                          borderRadius: '12px',
                          border: formData.water_tonnage === '20' ? '3px solid #06b6d4' : '2px solid #e2e8f0',
                          cursor: 'pointer',
                          flex: 1,
                          transition: 'all 0.2s'
                        }}>
                          <input
                            type="radio"
                            name="water_tonnage"
                            value="20"
                            checked={formData.water_tonnage === '20'}
                            onChange={(e) => setFormData({ ...formData, water_tonnage: e.target.value })}
                            style={{ display: 'none' }}
                          />
                          <span style={{ fontSize: '2rem' }}>🚿</span>
                          <span style={{ fontWeight: '700', fontSize: '1.25rem' }}>20 Ton</span>
                          <span style={{ 
                            color: '#0891b2', 
                            fontWeight: '700', 
                            fontSize: '1.1rem',
                            backgroundColor: '#cffafe',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '999px'
                          }}>
                            1,300 USD
                          </span>
                          <span style={{ color: '#64748b', fontSize: '0.8rem' }}>(65 USD x 20 ton)</span>
                        </label>
                      </div>
                    </div>
                  )}
                  
                  {/* Su Taşıma - Sadece İkmal seçildiğinde görünür */}
                  {formData.service_type === 'ikmal' && (
                    <div style={{ 
                      marginTop: '1.5rem', 
                      padding: '1rem', 
                      backgroundColor: formData.has_water ? '#ecfeff' : '#f8fafc', 
                      borderRadius: '8px',
                      border: formData.has_water ? '2px solid #06b6d4' : '1px solid #e2e8f0'
                    }}>
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.75rem', 
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '1rem'
                      }}>
                        <input
                          type="checkbox"
                          checked={formData.has_water}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            has_water: e.target.checked,
                            water_tonnage: e.target.checked ? '10' : ''
                          })}
                          style={{ 
                            width: '20px', 
                            height: '20px',
                            accentColor: '#06b6d4'
                          }}
                        />
                        <span>💧 Su Taşıma</span>
                      </label>
                      
                      {formData.has_water && (
                        <div style={{ marginTop: '1rem' }}>
                          <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                            Su Miktarı
                          </label>
                          <div style={{ display: 'flex', gap: '1rem' }}>
                            <label style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '0.5rem',
                              padding: '0.75rem 1rem',
                              backgroundColor: formData.water_tonnage === '10' ? '#cffafe' : '#f1f5f9',
                              borderRadius: '8px',
                              border: formData.water_tonnage === '10' ? '2px solid #06b6d4' : '1px solid #e2e8f0',
                              cursor: 'pointer',
                              flex: 1,
                              justifyContent: 'center'
                            }}>
                              <input
                                type="radio"
                                name="water_tonnage"
                                value="10"
                                checked={formData.water_tonnage === '10'}
                                onChange={(e) => setFormData({ ...formData, water_tonnage: e.target.value })}
                                style={{ accentColor: '#06b6d4' }}
                              />
                              <span style={{ fontWeight: '600' }}>10 Ton</span>
                              <span style={{ color: '#64748b', fontSize: '0.85rem' }}>(Yarım Ücret)</span>
                            </label>
                            <label style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '0.5rem',
                              padding: '0.75rem 1rem',
                              backgroundColor: formData.water_tonnage === '20' ? '#cffafe' : '#f1f5f9',
                              borderRadius: '8px',
                              border: formData.water_tonnage === '20' ? '2px solid #06b6d4' : '1px solid #e2e8f0',
                              cursor: 'pointer',
                              flex: 1,
                              justifyContent: 'center'
                            }}>
                              <input
                                type="radio"
                                name="water_tonnage"
                                value="20"
                                checked={formData.water_tonnage === '20'}
                                onChange={(e) => setFormData({ ...formData, water_tonnage: e.target.value })}
                                style={{ accentColor: '#06b6d4' }}
                              />
                              <span style={{ fontWeight: '600' }}>20 Ton</span>
                              <span style={{ color: '#64748b', fontSize: '0.85rem' }}>(Tam Ücret)</span>
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Yük Taşıma Checkbox - Tüm hizmet türlerinde görünür */}
                  <div style={{ 
                    marginTop: '1.5rem', 
                    padding: '1rem', 
                    backgroundColor: formData.has_cargo ? '#eff6ff' : '#f8fafc', 
                    borderRadius: '8px',
                    border: formData.has_cargo ? '2px solid #3b82f6' : '1px solid #e2e8f0'
                  }}>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '0.75rem', 
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: '1rem'
                    }}>
                      <input
                        type="checkbox"
                        checked={formData.has_cargo}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          has_cargo: e.target.checked,
                          cargo_tonnage: e.target.checked ? formData.cargo_tonnage : '',
                          cargo_rate: 70
                        })}
                        style={{ 
                          width: '20px', 
                          height: '20px',
                          accentColor: '#3b82f6'
                        }}
                      />
                      <span>📦 Yük Taşıma Var mı?</span>
                    </label>
                    
                    {formData.has_cargo && (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label htmlFor="cargo_tonnage" style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', fontSize: '0.9rem' }}>
                            Yük Tonajı (Ton) <span style={{ color: '#dc2626' }}>*</span>
                          </label>
                          <input
                            type="number"
                            id="cargo_tonnage"
                            className="form-control"
                            value={formData.cargo_tonnage}
                            onChange={(e) => setFormData({ ...formData, cargo_tonnage: e.target.value })}
                            placeholder="Örn: 5"
                            min="1"
                            step="1"
                            required
                            style={{ 
                              padding: '0.75rem',
                              fontSize: '1rem',
                              borderRadius: '8px',
                              borderColor: '#3b82f6',
                              backgroundColor: '#fff'
                            }}
                          />
                          <small style={{ color: '#64748b' }}>1 ve katları olarak giriniz</small>
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label htmlFor="cargo_rate" style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block', fontSize: '0.9rem' }}>
                            Ton Başı Ücret (USD)
                          </label>
                          <input
                            type="number"
                            id="cargo_rate"
                            className="form-control"
                            value={formData.cargo_rate}
                            onChange={(e) => setFormData({ ...formData, cargo_rate: e.target.value })}
                            min="1"
                            step="1"
                            style={{ 
                              padding: '0.75rem',
                              fontSize: '1rem',
                              borderRadius: '8px',
                              backgroundColor: '#fff'
                            }}
                          />
                          <small style={{ color: '#64748b' }}>Varsayılan: 70 USD/ton</small>
                        </div>
                        {formData.cargo_tonnage && (
                          <div style={{ 
                            gridColumn: '1 / -1', 
                            padding: '0.75rem', 
                            backgroundColor: '#dcfce7', 
                            borderRadius: '6px',
                            textAlign: 'center'
                          }}>
                            <strong style={{ color: '#166534' }}>
                              Taşıma Tutarı: ${(parseFloat(formData.cargo_tonnage) * parseFloat(formData.cargo_rate || 70)).toFixed(2)}
                            </strong>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* BÖLÜM 2: Gemi ve Müşteri Bilgileri */}
                <div style={{ 
                  backgroundColor: '#fff', 
                  borderRadius: '12px', 
                  padding: '1.5rem',
                  marginBottom: '1.5rem',
                  border: '1px solid #e2e8f0'
                }}>
                  <h3 style={{ 
                    fontSize: '1rem', 
                    fontWeight: '600', 
                    color: '#1a365d', 
                    marginBottom: '1.25rem',
                    paddingBottom: '0.75rem',
                    borderBottom: '2px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span style={{ fontSize: '1.2rem' }}>🚢</span> Gemi ve Müşteri Bilgileri
                  </h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.25rem' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label htmlFor="vessel_name" style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                        Gemi Adı <span style={{ color: '#dc2626' }}>*</span>
                      </label>
                      <input
                        type="text"
                        id="vessel_name"
                        className="form-control"
                        list="vessels-list"
                        value={formData.vessel_name}
                        onChange={(e) => setFormData({ ...formData, vessel_name: e.target.value })}
                        placeholder="Gemi adını giriniz veya seçiniz"
                        required
                        style={{ padding: '0.75rem', fontSize: '1rem', borderRadius: '8px' }}
                      />
                      <datalist id="vessels-list">
                        {vessels.map(vessel => (
                          <option key={vessel.id} value={vessel.name} />
                        ))}
                      </datalist>
                    </div>
                    
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label htmlFor="client_id" style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                        Müşteri / Acenta <span style={{ color: '#dc2626' }}>*</span>
                      </label>
                      <select
                        id="client_id"
                        className="form-control"
                        value={formData.client_id}
                        onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                        required
                        style={{ padding: '0.75rem', fontSize: '1rem', borderRadius: '8px' }}
                      >
                        <option value="">-- Müşteri Seçiniz --</option>
                        {clients.map(client => (
                          <option key={client.id} value={client.id}>{client.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Agent Seçimi - Motor kiralayan farklı acente */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.25rem' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label htmlFor="agent_id" style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                        Bill&apos;de Gösterilecek Acente <span style={{ color: '#64748b', fontWeight: 'normal' }}>(Opsiyonel)</span>
                      </label>
                      <select
                        id="agent_id"
                        className="form-control"
                        value={formData.agent_id}
                        onChange={(e) => setFormData({ ...formData, agent_id: e.target.value })}
                        style={{ padding: '0.75rem', fontSize: '1rem', borderRadius: '8px' }}
                      >
                        <option value="">-- Varsayılan (Rota Maritime) --</option>
                        {clients.map(client => (
                          <option key={client.id} value={client.id}>{client.name}</option>
                        ))}
                      </select>
                      <small style={{ color: '#64748b' }}>Farklı bir acente için motor kiralandıysa seçiniz</small>
                    </div>
                    
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label htmlFor="location" style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                        Lokasyon <span style={{ color: '#dc2626' }}>*</span>
                      </label>
                      <select
                        id="location"
                        className="form-control"
                        value={formData.location}
                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                        required
                        style={{ padding: '0.75rem', fontSize: '1rem', borderRadius: '8px' }}
                      >
                        {motorLaunchRates.length > 0 ? (
                          motorLaunchRates.map((rate, idx) => (
                            <option key={idx} value={`${rate.name_tr} / ${rate.name_en}`}>
                              📍 {rate.name_tr} / {rate.name_en} - ${rate.rate}
                            </option>
                          ))
                        ) : (
                          <>
                            <option value="1. Demirsahası / 1st Anchorage Area">📍 1. Demirsahası / 1st Anchorage Area - $350</option>
                            <option value="3. Demirsahası / 3rd Anchorage Area">📍 3. Demirsahası / 3rd Anchorage Area - $450</option>
                          </>
                        )}
                      </select>
                    </div>
                  </div>
                </div>
                
                {/* BÖLÜM 3: Sefer Detayları */}
                <div style={{ 
                  backgroundColor: '#fff', 
                  borderRadius: '12px', 
                  padding: '1.5rem',
                  marginBottom: '1.5rem',
                  border: '1px solid #e2e8f0'
                }}>
                  <h3 style={{ 
                    fontSize: '1rem', 
                    fontWeight: '600', 
                    color: '#1a365d', 
                    marginBottom: '1.25rem',
                    paddingBottom: '0.75rem',
                    borderBottom: '2px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span style={{ fontSize: '1.2rem' }}>📅</span> Sefer Detayları
                  </h3>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.25rem' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label htmlFor="trip_date" style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                        Sefer Tarihi <span style={{ color: '#dc2626' }}>*</span>
                      </label>
                      <input
                        type="date"
                        id="trip_date"
                        className="form-control"
                        value={formData.trip_date}
                        onChange={(e) => setFormData({ ...formData, trip_date: e.target.value })}
                        required
                        style={{ padding: '0.75rem', fontSize: '1rem', borderRadius: '8px' }}
                      />
                    </div>
                    
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label htmlFor="captain_name" style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                        Kaptan Adı
                      </label>
                      <input
                        type="text"
                        id="captain_name"
                        className="form-control"
                        value={formData.captain_name}
                        onChange={(e) => setFormData({ ...formData, captain_name: e.target.value })}
                        placeholder="Kaptan adını giriniz (opsiyonel)"
                        style={{ padding: '0.75rem', fontSize: '1rem', borderRadius: '8px' }}
                      />
                    </div>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label htmlFor="departure_time" style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                        🕐 Gidiş Saati <span style={{ color: '#dc2626' }}>*</span>
                      </label>
                      <input
                        type="time"
                        id="departure_time"
                        className="form-control"
                        value={formData.departure_time}
                        onChange={(e) => setFormData({ ...formData, departure_time: e.target.value })}
                        required
                        style={{ padding: '0.75rem', fontSize: '1rem', borderRadius: '8px' }}
                      />
                    </div>
                    
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label htmlFor="return_time" style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                        🕐 Dönüş Saati <span style={{ color: '#dc2626' }}>*</span>
                      </label>
                      <input
                        type="time"
                        id="return_time"
                        className="form-control"
                        value={formData.return_time}
                        onChange={(e) => setFormData({ ...formData, return_time: e.target.value })}
                        required
                        style={{ padding: '0.75rem', fontSize: '1rem', borderRadius: '8px' }}
                      />
                    </div>
                    
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label htmlFor="waiting_hours" style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>
                        ⏱️ Bekleme Süresi (Saat)
                      </label>
                      <input
                        type="number"
                        id="waiting_hours"
                        className="form-control"
                        step="0.5"
                        min="0"
                        value={formData.waiting_hours}
                        onChange={(e) => setFormData({ ...formData, waiting_hours: e.target.value })}
                        placeholder="0"
                        style={{ padding: '0.75rem', fontSize: '1rem', borderRadius: '8px' }}
                      />
                    </div>
                  </div>
                </div>
                
                {/* BÖLÜM 4: Notlar (Opsiyonel) */}
                <div style={{ 
                  backgroundColor: '#fff', 
                  borderRadius: '12px', 
                  padding: '1.5rem',
                  border: '1px solid #e2e8f0'
                }}>
                  <h3 style={{ 
                    fontSize: '1rem', 
                    fontWeight: '600', 
                    color: '#1a365d', 
                    marginBottom: '1.25rem',
                    paddingBottom: '0.75rem',
                    borderBottom: '2px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <span style={{ fontSize: '1.2rem' }}>📝</span> Ek Notlar <span style={{ fontWeight: '400', color: '#64748b', fontSize: '0.875rem' }}>(Opsiyonel)</span>
                  </h3>
                  
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <textarea
                      id="notes"
                      className="form-control"
                      rows="3"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Sefer ile ilgili eklemek istediğiniz notları buraya yazabilirsiniz..."
                      style={{ padding: '0.75rem', fontSize: '1rem', borderRadius: '8px', resize: 'vertical' }}
                    />
                  </div>
                </div>
                
              </div>
              
              {/* Footer */}
              <div className="modal-footer" style={{ 
                padding: '1.25rem 2rem', 
                backgroundColor: '#fff',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '1rem'
              }}>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowModal(false)}
                  style={{ 
                    padding: '0.75rem 2rem', 
                    fontSize: '1rem',
                    borderRadius: '8px',
                    minWidth: '120px'
                  }}
                >
                  İptal
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  style={{ 
                    padding: '0.75rem 2rem', 
                    fontSize: '1rem',
                    borderRadius: '8px',
                    minWidth: '160px',
                    background: 'linear-gradient(135deg, #1a365d 0%, #2563eb 100%)',
                    border: 'none'
                  }}
                >
                  {editingLaunch ? '✓ Güncelle' : '✓ Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MotorLaunches;
